import React, { useEffect, useState } from 'react';
import {
  Card, Descriptions, Table, Tag, Button, Space, Typography,
  message, Modal, Divider, Result, Statistic, Row, Col,
} from 'antd';
import {
  SendOutlined, CheckCircleOutlined, SwapOutlined,
  CloseCircleOutlined, EditOutlined, ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { quotationApi, workflowApi } from '../../services/api';
import useAuthStore from '../../store/authStore';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const STATUS_COLORS = {
  '草稿': 'default', '已发送': 'blue', '客户确认': 'orange',
  '已成交': 'green', '已失效': 'red',
};

const QUOTATION_TYPES = { '产品销售': '产品销售合同', '项目工程': '工程合同', '运维服务': '运维合同' };

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [converted, setConverted] = useState(null);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await quotationApi.get(id);
      setQuotation(res);
    } catch (e) {
      message.error(e?.error || '获取报价详情失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetail(); }, [id]);

  const handleAction = async (action, confirmMsg) => {
    if (confirmMsg) {
      Modal.confirm({
        title: '确认操作',
        content: confirmMsg,
        onOk: async () => {
          await doAction(action);
        },
      });
    } else {
      await doAction(action);
    }
  };

  const doAction = async (action) => {
    setActionLoading(true);
    try {
      switch (action) {
        case 'send':
          await quotationApi.send(id);
          message.success('报价已发送');
          break;
        case 'confirm':
          await quotationApi.confirm(id);
          message.success('客户已确认');
          break;
        case 'convert': {
          const res = await quotationApi.convert(id);
          message.success('已生成合同');
          setConverted(res);
          break;
        }
        case 'cancel':
          await quotationApi.cancel(id);
          message.success('报价已作废');
          break;
        default:
          break;
      }
      fetchDetail();
    } catch (e) {
      message.error(e?.error || '操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Card loading />;

  if (!quotation) return <Result status="404" title="报价不存在" />;

  // 已生成合同
  if (converted) {
    return (
      <Result
        status="success"
        title="合同已生成"
        subTitle={`报价 ${quotation.code} 已成功转化为合同`}
        extra={[
          <Button key="contract" type="primary" onClick={() => navigate(`/contracts/${converted.contractId}`)}>
            查看合同
          </Button>,
          <Button key="list" onClick={() => navigate('/quotations')}>返回报价列表</Button>,
        ]}
      />
    );
  }

  const isOwner = user?.role === 'admin' || user?.role === 'manager' || quotation.userId === user?.id;

  const itemColumns = [
    { title: '序号', key: 'idx', width: 60, render: (_, __, idx) => idx + 1 },
    {
      title: '产品名称', dataIndex: 'name', key: 'name',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          {r.product && <Text type="secondary" style={{ fontSize: 12 }}>{r.product.sku}</Text>}
        </Space>
      ),
    },
    { title: '规格', dataIndex: 'spec', key: 'spec', render: (v) => v || '—' },
    { title: '数量', dataIndex: 'quantity', key: 'quantity' },
    { title: '单位', dataIndex: 'unit', key: 'unit' },
    {
      title: '单价', dataIndex: 'unitPrice', key: 'unitPrice',
      render: (v) => v.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' }),
    },
    {
      title: '折扣', dataIndex: 'discount', key: 'discount',
      render: (v) => `${(v * 100).toFixed(0)}%`,
    },
    {
      title: '小计', dataIndex: 'subtotal', key: 'subtotal',
      render: (v) => v.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' }),
    },
  ];

  const subtotalSum = quotation.items.reduce((s, i) => s + i.subtotal, 0);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* 顶部 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/quotations')}>返回</Button>
          <Title level={4} style={{ margin: 0 }}>报价详情</Title>
          <Tag color={STATUS_COLORS[quotation.status]} style={{ fontSize: 14, padding: '2px 12px' }}>
            {quotation.status}
          </Tag>
          <Text type="secondary" style={{ fontSize: 14 }}>{quotation.code}</Text>
        </Space>

        <Space>
          {quotation.status === '草稿' && (
            <>
              <Button icon={<EditOutlined />} onClick={() => navigate(`/quotations/new?edit=${id}`)}>
                编辑
              </Button>
              <Button type="primary" icon={<SendOutlined />} onClick={() => handleAction('send', '确认发送该报价？')}
                loading={actionLoading}>发送</Button>
            </>
          )}
          {quotation.status === '已发送' && (
            <>
              <Button type="primary" icon={<CheckCircleOutlined />}
                onClick={() => handleAction('confirm', '确认客户已确认该报价？')}
                loading={actionLoading}>客户确认</Button>
              <Button danger icon={<CloseCircleOutlined />}
                onClick={() => handleAction('cancel', '确认作废该报价？')}
                loading={actionLoading}>作废</Button>
            </>
          )}
          {quotation.status === '客户确认' && (
            <>
              <Button type="primary" icon={<SwapOutlined />}
                onClick={() => handleAction('convert', '确认将该报价转为合同？')}
                loading={actionLoading}>转合同</Button>
              <Button danger icon={<CloseCircleOutlined />}
                onClick={() => handleAction('cancel', '确认作废该报价？')}
                loading={actionLoading}>作废</Button>
            </>
          )}
        </Space>
      </div>

      {/* 基本信息 */}
      <Card size="small" title="基本信息" style={{ marginBottom: 16 }}>
        <Descriptions column={3} bordered size="small">
          <Descriptions.Item label="报价编号">{quotation.code}</Descriptions.Item>
          <Descriptions.Item label="客户">{quotation.customer?.name || '—'}</Descriptions.Item>
          <Descriptions.Item label="联系人">{quotation.customer?.contact || '—'}</Descriptions.Item>
          <Descriptions.Item label="报价类型">{quotation.type}</Descriptions.Item>
          <Descriptions.Item label="税率">{Math.round(quotation.taxRate * 100)}%</Descriptions.Item>
          <Descriptions.Item label="有效期至">
            {quotation.validUntil ? dayjs(quotation.validUntil).format('YYYY-MM-DD') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="创建人">{quotation.user?.name}</Descriptions.Item>
          <Descriptions.Item label="创建时间">{dayjs(quotation.createdAt).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
          <Descriptions.Item label="备注">{quotation.notes || '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 产品明细 */}
      <Card size="small" title="产品明细" style={{ marginBottom: 16 }}>
        <Table
          columns={itemColumns}
          dataSource={quotation.items}
          rowKey="id"
          pagination={false}
          size="small"
        />

        <Divider />
        <Row justify="end">
          <Col>
            <Space direction="vertical" align="end">
              <Text>产品小计：<Text strong>{subtotalSum.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' })}</Text></Text>
              <Text>税率：{Math.round(quotation.taxRate * 100)}%</Text>
              <Text style={{ fontSize: 18 }}>
                含税总额：<Text strong style={{ color: '#ff4d4f' }}>{quotation.totalAmount.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' })}</Text>
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 金额统计 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic title="产品总额" value={subtotalSum} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic title="税额" value={subtotalSum * quotation.taxRate} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic title="含税总额" value={quotation.totalAmount} precision={2} prefix="¥"
              valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
